import { useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import { useTenant } from '@/tenant/TenantProvider'
import { useViewStore } from '@/state/useViewStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { SLIDE_REGISTRY } from '@/slides/SlideRegistry'
import { useTranslation } from 'react-i18next'

export function SlideCarousel() {
  const { t } = useTranslation()
  const tenant = useTenant()
  const settings = useSettingsStore()
  const slideIndex = useViewStore((s) => s.slideIndex)
  const advance = useViewStore((s) => s.advanceSlide)
  const setSlideIndex = useViewStore((s) => s.setSlideIndex)
  const paused = useViewStore((s) => s.paused)
  const viewMode = useViewStore((s) => s.viewMode)

  const enabled = tenant.slides.enabled
  const slideEntries = useMemo(() => enabled.map((id) => SLIDE_REGISTRY[id]), [enabled])
  const currentIdx = slideIndex % Math.max(1, slideEntries.length)
  const current = slideEntries[currentIdx]
  const duration = useMemo(() => {
    if (settings.slideIntervalSec) return settings.slideIntervalSec
    const fromOverride = settings.perSlideOverrides[current.id]
    if (fromOverride) return fromOverride
    const fromTenant = tenant.slides.overrides?.[current.id]?.durationSec
    if (fromTenant) return fromTenant
    return current.defaultDurationSec
  }, [settings.slideIntervalSec, settings.perSlideOverrides, tenant, current])

  const progressRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (paused || viewMode !== 'main') return
    const bar = progressRef.current
    if (bar) {
      bar.style.transition = 'none'
      bar.style.width = '0%'
      void bar.offsetWidth
      bar.style.transition = `width ${duration}s linear`
      bar.style.width = '100%'
    }
    const id = window.setTimeout(() => advance(enabled), duration * 1000)
    return () => window.clearTimeout(id)
  }, [paused, viewMode, currentIdx, duration, advance, enabled])

  // Ruční přeskakování šipkami. Časovač se tím restartuje (efekt výš závisí
  // na currentIdx), takže po přepnutí je na slide zase celý interval.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      // Nepřebírat šipky, když uživatel píše do pole nastavení.
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
        return
      }
      if (viewMode !== 'main') return
      e.preventDefault()
      const count = Math.max(1, slideEntries.length)
      const delta = e.key === 'ArrowRight' ? 1 : -1
      setSlideIndex((((currentIdx + delta) % count) + count) % count)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [currentIdx, slideEntries.length, setSlideIndex, viewMode])

  const Component = current.Component
  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {}
      <div className="flex shrink-0 items-center justify-between pb-2">
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink">
          {t(current.titleKey)}
        </h2>
        <div className="flex items-center gap-2 text-xs text-ink-subtle">
          <span className="font-mono">
            {currentIdx + 1}/{slideEntries.length}
          </span>
        </div>
      </div>
      {}
      <div className="mb-3 h-0.5 shrink-0 overflow-hidden rounded-full bg-border">
        <div
          ref={progressRef}
          className="h-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-accent)]"
          style={{ width: '0%' }}
        />
      </div>

      {}
      <motion.div
        key={current.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <Component />
      </motion.div>
    </div>
  )
}
